import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Heart, User, TrendingUp, Settings2, LineChart, Cpu, Lightbulb } from "lucide-react";
import { HERO_BADGES } from "../data/content";
import HeroNetworkAnimation from "./HeroNetworkAnimation";
import { openOurStoryModal } from "./OurStoryModal";
import { openAgent } from "../utils/openAgent";

const ICONS = { Heart, User, TrendingUp, Settings2, LineChart, Cpu, Lightbulb };

const Badge = ({ b }) => {
  const Icon = ICONS[b.icon] || Heart;
  return (
    <div
      className="absolute flex items-center gap-[10px] whitespace-nowrap rounded-[12px] border border-[#284471]/80 px-[14px] py-2 shadow-lg animate-[floaty_6s_ease-in-out_infinite] backdrop-blur-[2px]"
      style={{
        left: `${(b.left / 794) * 100}%`,
        top: `${(b.top / 611) * 100}%`,
        background: "radial-gradient(ellipse 53% 90% at 50% 50%, rgba(1,19,46,0.92) 0%, rgba(17,43,83,0.92) 100%)",
        animationDelay: `${(b.left % 5) * 0.3}s`,
      }}
    >
      <div className="flex h-7 w-7 items-center justify-center rounded-[8px]" style={{ background: b.iconBg }}>
        <Icon className="h-4 w-4 text-white" strokeWidth={1.6} />
      </div>
      <div className="flex flex-col">
        <span className="text-[12px] font-bold leading-[12px] text-white">{b.label}</span>
        <span className="text-[10px] font-medium leading-[15px] text-white/80">{b.sub}</span>
      </div>
    </div>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  const handleDiscoverClick = () => {
    openAgent();
  };

  return (
    <section id="why" className="relative flex min-h-[100dvh] w-full items-center overflow-hidden" style={{ background: "#010710" }}>
      {/* Embedded keyframes for the floating badge animation */}
      <style>{`
        @keyframes floaty {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
      `}</style>

      {/* top-left glow */}
      <div
        className="pointer-events-none absolute"
        style={{ width: 1187, height: 345, left: -238, top: -89, background: "#173862", filter: "blur(92.65px)" }}
      />

      <div className="relative mx-auto w-full max-w-[1600px] px-6 py-20 lg:px-12 xl:px-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8 xl:gap-12">

          {/* Left copy: balanced 6 columns for comfortable headline fit */}
          <div className="flex w-full flex-col gap-[28px] lg:col-span-6 z-10">
            <div className="flex flex-col gap-6">
              {/* Eyebrow Tag */}
              <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-white/80 sm:text-[12px]">
                THE AI FACTORY
              </p>

              {/* Headline matching exact 3 lines and cyan-blue gradient */}
              <h1 className="text-[28px] min-[400px]:text-[34px] sm:text-[42px] md:text-[46px] lg:text-[46px] xl:text-[54px] font-bold leading-[1.12] tracking-tight text-white">
                <span className="block whitespace-nowrap">Every business is</span>
                <span className="block whitespace-nowrap">becoming an AI business.</span>
                <span className="inline-block whitespace-nowrap bg-gradient-to-r from-[#00D2EE] to-[#1E88E5] bg-clip-text text-transparent pb-1 pr-4">
                  We help you get there.
                </span>
              </h1>

              {/* Subheading */}
              <p className="max-w-[480px] text-[15px] sm:text-[16px] xl:text-[17px] font-normal leading-relaxed text-[#CBD5E1]">
                From opportunity to ongoing impact—
                <br />
                AI that understands your business.
              </p>
            </div>

            {/* Buttons matching screenshot */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2 sm:pt-3">
              {/* Glowing White Pill Button matching exact screenshot */}
              <button
                onClick={handleDiscoverClick}
                className="group flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 sm:px-8 sm:py-3.5 text-[14.5px] sm:text-[15px] font-semibold text-[#0B132B] shadow-[0_10px_24px_-2px_rgba(29,78,216,0.55),0_4px_10px_rgba(37,99,235,0.35)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_28px_-2px_rgba(29,78,216,0.65),0_4px_12px_rgba(37,99,235,0.4)] active:scale-95 cursor-pointer"
              >
                <span>Discover Your AI Opportunities</span>
                <ArrowRight
                  className="h-4 w-4 text-[#0B132B] transition-transform duration-300 group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </button>

              {/* Our Story Button */}
              <button
                onClick={openOurStoryModal}
                className="group flex items-center gap-3 py-2 cursor-pointer transition-opacity duration-200 hover:opacity-90"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/5 transition-all duration-300 group-hover:border-white/50 group-hover:bg-white/10 group-hover:scale-105">
                  <Play className="h-4 w-4 fill-white text-white ml-0.5" />
                </span>
                <span className="text-[14px] sm:text-[15px] font-medium text-white">
                  Our Story
                </span>
              </button>
            </div>
          </div>

          {/* Right AI Graphic + Futuristic Neon Network Animation */}
          <div className="relative w-full lg:col-span-6 flex items-center justify-center lg:justify-end">
            <div className="relative w-full max-w-[620px] lg:max-w-[700px] xl:max-w-[780px]" style={{ aspectRatio: "1024 / 665" }}>
              <img
                src="/images/hero.png"
                alt="AI Connected Enterprise"
                className="w-full h-full object-contain pointer-events-none select-none"
                style={{
                  mixBlendMode: "screen",
                  WebkitMaskImage: "radial-gradient(ellipse 95% 90% at 50% 50%, black 65%, transparent 100%)",
                  maskImage: "radial-gradient(ellipse 95% 90% at 50% 50%, black 65%, transparent 100%)",
                }}
              />

              {/* Futuristic neon energy-flow animation overlay */}
              <HeroNetworkAnimation />

              {/* Desktop absolute floating badges around the network */}
              <div className="absolute inset-0 hidden lg:block pointer-events-none">
                {HERO_BADGES.map((b) => (
                  <Badge key={b.label} b={b} />
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;